import { useMemo, useState } from 'react'
import { Link, instructorLinkState, instructorPath, useSharedAvatarStyle } from '@/components/layout/TransitionLink'
import type { InstructorSummary } from '@/hooks/useAllInstructors'
import { Search } from 'lucide-react'
import { motion } from 'motion/react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { TopInstructorsCarousel } from '@/components/instructor/TopInstructorsCarousel'
import { NameMarquee } from '@/components/dashboard/fx/NameMarquee'
import { PageHero } from '@/components/dashboard/fx/PageHero'
import { SpotlightCard } from '@/components/dashboard/fx/SpotlightCard'
import { EASE } from '@/components/dashboard/fx/motion'
import { PAGE_WIDTH } from '@/components/dashboard/fx/layout'
import { useAllInstructors } from '@/hooks/useAllInstructors'
import { formatPrecise, initials } from '@/lib/format'
import { workoutNouns } from '@/lib/discipline'

// This list mixes instructors across every discipline (many teach more
// than one), so "workouts" rather than a discipline-specific noun.
const { plural: WORKOUTS } = workoutNouns('all')

// The API already returns instructors ranked by ride_count desc, so the
// "top" ones are just the front of the list -- no separate fetch needed.
const TOP_INSTRUCTORS_COUNT = 10

function InstructorTile({ instructor, index, shareName }: { instructor: InstructorSummary; index: number; shareName: boolean }) {
  const to = instructorPath(instructor.id)
  // The top-10 carousel above already carries the name for these people --
  // a duplicate view-transition-name on one page cancels the transition.
  const shared = useSharedAvatarStyle(to, instructor.id, shareName)
  return (
    <motion.li
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '0px 0px -5% 0px' }}
      transition={{ duration: 0.6, delay: Math.min(index % 8, 7) * 0.04, ease: EASE }}
    >
      <Link to={to} state={instructorLinkState(instructor.name, instructor.image_url)} className="group block h-full">
        <SpotlightCard className="flex h-full items-center gap-3 rounded-2xl p-4 transition-transform duration-300 group-hover:-translate-y-1">
          <Avatar style={shared} className="size-12 shrink-0 grayscale transition duration-500 group-hover:grayscale-0">
            <AvatarImage src={instructor.image_url ?? undefined} alt="" />
            <AvatarFallback className="text-xs">{initials(instructor.name)}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-medium">{instructor.name}</span>
            <span className="text-xs text-muted-foreground">
              {formatPrecise(instructor.ride_count)} {WORKOUTS}
            </span>
          </div>
        </SpotlightCard>
      </Link>
    </motion.li>
  )
}

export function InstructorsListPage() {
  const { data, error } = useAllInstructors()
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    if (!data) return null
    const q = query.trim().toLowerCase()
    return q ? data.filter((i) => i.name.toLowerCase().includes(q)) : data
  }, [data, query])

  return (
    <div className="overflow-x-clip">
      <PageHero
        eyebrow="Directory"
        title="Every"
        accent="instructor."
        description={data ? `${formatPrecise(data.length)} instructors tracked across every discipline.` : 'Loading...'}
      >
        {data && data.length > 0 && (
          <div className="pb-8">
            <NameMarquee compact duration={110} words={data.slice(0, 30).map((i) => i.name)} />
          </div>
        )}
      </PageHero>

      <div className={`${PAGE_WIDTH} flex flex-col gap-12 py-12`}>
        {error && <p className="text-sm text-destructive">Couldn&apos;t load instructors: {error}</p>}

        {/* Only while not actively searching -- a curated highlight doesn't
            make sense mixed in with filtered search results. */}
        {data && !query && data.length > 0 && <TopInstructorsCarousel instructors={data.slice(0, TOP_INSTRUCTORS_COUNT)} />}

        <div className="flex flex-col gap-6">
          <div className="relative max-w-md">
            <Search className="pointer-events-none absolute left-4 top-1/2 z-10 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search instructors..."
              className="h-12 rounded-full bg-card/50 pl-11 backdrop-blur"
            />
          </div>

          {filtered ? (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {filtered.map((instructor, i) => (
                <InstructorTile
                  key={instructor.id}
                  instructor={instructor}
                  index={i}
                  shareName={Boolean(query) || i >= TOP_INSTRUCTORS_COUNT}
                />
              ))}
              {filtered.length === 0 && (
                <li className="col-span-full text-sm text-muted-foreground">No instructors match &ldquo;{query}&rdquo;</li>
              )}
            </ul>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <Skeleton key={i} className="h-[82px] rounded-2xl" />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
